import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle In-Dialog-Tour fuer den Schicht-Dialog
 * (modules/admin/component/add-employee-in-work-shift). Wird via Hilfe-Button
 * (`<app-dialog-help-button>`) im Dialog-Header manuell gestartet. Erklaert in
 * 5 Schritten: Welcome → Arbeitsstunden (Zeit + Pause-Regeln) → Abteilung &
 * Qualifizierung → Mitarbeiter (Abwesenheits-Hinweise + Farb-Hierarchie) →
 * Speichern (Entwurf-Status + Sperr-Logik).
 *
 * Pattern analog zu `SHIFT_PLAN_ANALYSIS_HELP_TOUR` / `AUTO_SUGGEST_HELP_TOUR`:
 * erklaerungs-getrieben (manuelles Weiterklicken, `nextCondition: 'manual'`),
 * nicht formular-gebunden.
 *
 * Saemtliche Anchors existieren bereits in der Registry (Abschnitt
 * "SHIFTS PLANNER — Add-Shift Dialog") und im Template — die grosse
 * `SHIFT_PLANNING_ONBOARDING_TOUR` nutzt vier davon ebenfalls (geteilte
 * Ressource, kein Konflikt).
 *
 * Fachliche Quelle (Backend): `AddEventCommandHandler` (Status immer Draft,
 * sichtbar erst nach Publish; Pause 30 min / ab 9 h 45 min; End>Start +
 * Max-Tagesarbeitszeit sperren Save), `hasBlockingAbsence()`
 * (add-employee-in-work-shift.component.ts): AbsenceTypes 1/2/3/4/7/8 blockieren,
 * 5 (Dienstreise) / 6 (Gleittag) nicht. Closed-Day ist rein informativ.
 */

export const ADD_SHIFT_HELP_TOUR: TourConfig = {
  id: TourFeature.AddShiftHelp,
  version: 1,
  title: 'ADD_SHIFT_HELP_TOUR_TITLE',
  description: 'ADD_SHIFT_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Schichtzuweisung navigieren. Der Schicht-Dialog hat
  // KEINEN deterministischen Opener-Button — er oeffnet via Klick auf eine
  // leere Kalenderzelle. Daher `route` only, ohne `opens`. Wenn der User
  // schon im Dialog ist (In-Dialog-`?`-Button), uebernimmt der bestehende
  // Pfad. Phase 2 koennte einen dynamischen "erste leere Zelle"-Anker
  // einfuehren.
  launchContext: {
    route: '/planner',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 70,
  // Bewusst 'feature-discovery' (NICHT 'contextual-help'): klassische Guided
  // Tour mit Spotlight + Voice-Over, nicht der HelpModeService mit (i)-Icons.
  // Begruendung siehe AUTO_SUGGEST_HELP_TOUR.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'ADD_SHIFT_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Welcome erklaert den GANZEN Dialog — Spotlight am .shift-dialog-container.
          anchor: AnchorId.ShiftDialog,
          title: 'TUTORIAL.ADD_SHIFT.WELCOME.TITLE',
          content: 'TUTORIAL.ADD_SHIFT.WELCOME.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'time',
          anchor: AnchorId.ShiftTimeSection,
          title: 'TUTORIAL.ADD_SHIFT.TIME.TITLE',
          content: 'TUTORIAL.ADD_SHIFT.TIME.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'assignment',
          // Spotlight umfasst Abteilung UND Qualifizierung — beide stehen
          // nebeneinander im selben Grid und gehoeren fachlich zusammen.
          anchor: AnchorId.ShiftDepartmentSelect,
          additionalSpotlightAnchors: [AnchorId.ShiftSkillSelect],
          title: 'TUTORIAL.ADD_SHIFT.ASSIGNMENT.TITLE',
          content: 'TUTORIAL.ADD_SHIFT.ASSIGNMENT.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'employee',
          anchor: AnchorId.ShiftEmployeeSelect,
          title: 'TUTORIAL.ADD_SHIFT.EMPLOYEE.TITLE',
          content: 'TUTORIAL.ADD_SHIFT.EMPLOYEE.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Im Open-Shift-Modus ohne "direkt zuweisen"-Toggle wird der
          // Mitarbeiter-Picker nicht gerendert (*ngIf). DOM-realer Check statt
          // vermutetem Attribut — siehe CONTEXTUAL_HELP_PATTERN-Stolperfallen.
          skipIf: () =>
            !document.querySelector(
              `[onboardingAnchor="${AnchorId.ShiftEmployeeSelect}"]`
            ),
        },
        {
          id: 'save',
          anchor: AnchorId.SaveShiftButton,
          title: 'TUTORIAL.ADD_SHIFT.SAVE.TITLE',
          content: 'TUTORIAL.ADD_SHIFT.SAVE.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Letzter Step: sauberer Abschluss, kein Zurueck-Button.
          showPrevButton: false,
        },
      ],
    },
  ],
};
